import type { RouteLocationNormalized } from '#vue-router'
import type { UseAuthentication } from '~/types/rental-api-aliases'

/**
 * Adds an action to Datadog.
 *
 * @param name The name of the action.
 * @param context The context of the action.
 */
export function datadogAddAction(name?: string, context?: unknown) {
  if (!name) {
    return datadogAddError('datadogAddAction: name is required', context)
  }

  window?.DD_RUM?.onReady(() => {
    window?.DD_RUM?.addAction(name, context)
  })
}

/**
 * Adds an error to Datadog.
 *
 * @param error The error to add.
 * @param context The context of the error.
 */
export function datadogAddError(error: unknown, context?: unknown) {
  log('datadogAddError', { error })

  window?.DD_RUM?.onReady(() => {
    window?.DD_RUM?.addError(
      error as Record<string, unknown>,
      context ? context : { cause: (error as Record<string, unknown>)?.message ?? 'Unknown' },
    )
  })
}

/**
 * Starts a view in Datadog.
 *
 * @param to The to route.
 * @param from The from route.
 */
export function datadogStartView({ to, from }: { to: RouteLocationNormalized, from: RouteLocationNormalized }) {
  log('datadogStartView', { to, from })

  let path = to.path || '/'

  if (to.matched?.length) {
    const matchedPath = to.matched[to.matched.length - 1]?.path || '/'

    // Replace ':slug' with '?'
    let result = matchedPath.replace(/:slug/g, '?')

    // Replace '()' with ''.
    result = result.replace(/\(\)/g, '')

    // Remove any trailing slashes if they exist
    path = result.replace(/\/$/, '')
  }

  window?.DD_RUM?.onReady(() => {
    window?.DD_RUM?.startView({
      name: path,
    })
  })
}

/**
 * Sets a user in Datadog.
 *
 * @param user The user to set.
 */
export function datadogSetUser({ user }: { user: UseAuthentication }) {
  log('datadogSetUser', { user })

  window?.DD_RUM?.onReady(() => {
    window?.DD_RUM?.setUser({
      id: user.Id,
      name: `${user.FirstName} ${user.LastName}`,
      email: user.Email,
    })
  })
}

/**
 * Removes a user from Datadog.
 */
export function datadogRemoveUser() {
  log('datadogRemoveUser')

  window?.DD_RUM?.onReady(() => {
    window?.DD_RUM?.clearUser()
  })
}
